import {
  buildSympyCode,
  nodeJsonPlainText,
  parseNodeToSympy,
  parseSympyResult,
  resolveKnownUnit,
} from "@corca/sympy";

import { mapUnitToSetChildren } from "@/components/doc/right-sidebar/active-node-style/fields/unitUtils";
import { pyodideManager } from "@/components/pyodide/PyodideManager";
import { setNodeChildren } from "@/components/suggestions/utils";
import { createLogger } from "@/utils/logger";
import { trySympy } from "@/utils/sympyErrors";

import type { ConvertCandidate } from "@/input/utils/convertCandidates";
import type { NumberNodeModel } from "@/models/nodes/NumberNodeModel";
import type { NodeJson } from "@corca/data";

export { filterConvertCandidates, getConvertUnitInfo } from "@/input/utils/convertCandidates";
export type { ConvertCandidate } from "@/input/utils/convertCandidates";

const logger = createLogger("ConvertUnitAction");

function extractMagnitude(nodes: NodeJson[], targetDisplay: string): string | null {
  if (nodes.length !== 1) return null;
  const [result] = nodes;

  if (result.type === "unit") {
    return nodeJsonPlainText(result) === targetDisplay ? "1" : null;
  }

  if (result.type !== "number") return null;
  const unitChild = result.children.find((c) => c.type === "unit");
  if (!unitChild || nodeJsonPlainText(unitChild) !== targetDisplay) return null;
  return String((result.props as { value: unknown }).value);
}

export async function runConvertUnitAction(
  node: NumberNodeModel,
  candidate: ConvertCandidate,
): Promise<boolean> {
  if (!pyodideManager.isReady) return false;

  const targetRef = resolveKnownUnit(candidate.display);
  if (!targetRef) return false;

  const inlineNodeJson: NodeJson = { type: "inline", props: {}, children: [node.toJSON()] };

  const parsed = trySympy(() => parseNodeToSympy(inlineNodeJson));
  if ("error" in parsed) return false;
  if (!parsed.expr) return false;
  parsed.warnings.forEach((warning) => logger.warn(warning));

  const build = trySympy(() =>
    buildSympyCode("convert", parsed.expr, parsed.variables, {
      indexedBases: parsed.indexedBases,
      convertTargetExpr: targetRef,
    }),
  );
  if ("error" in build) return false;

  let result;
  try {
    result = await pyodideManager.executeSympyCode(build.expr);
  } catch (e) {
    logger.warn("Execute error", e);
    return false;
  }
  const sympyResult = result.result;
  if (!sympyResult) return false;

  const parsedResult = trySympy(() => parseSympyResult(sympyResult, undefined, parsed.symbolNodes));
  if ("error" in parsedResult) {
    logger.warn("Parse result error", parsedResult.error);
    return false;
  }
  parsedResult.warnings.forEach((warning) => logger.warn(warning));

  const magnitude = extractMagnitude(parsedResult.nodes, candidate.display);
  if (magnitude === null) return false;
  if (Number(magnitude) === 0 && Number(node.value) !== 0) return false;

  const { doc, unitNode } = node;
  const unitWithType = { ...candidate.unit, unitType: unitNode?.unitType ?? "measurement" };
  doc.transact(() => {
    node.value = magnitude;
    setNodeChildren(doc, node, mapUnitToSetChildren(unitWithType));
  }, "ConvertField:convert");

  return true;
}
